/**
 * Marina administration: the water, the waiting list, the fleet, the
 * crew and the rate card.
 *
 * Five tabs because a dockmaster's year has five arguments in it, and
 * each one has a screen: what the club owns, who is waiting for it,
 * what it charges, what floats, and whose paperwork is about to run out.
 */

import { useState, type ReactNode } from 'react';
import { api } from '../lib/api.ts';
import { useApi, useMutation } from '../lib/useApi.ts';
import { useSession } from '../lib/session.tsx';
import { Card, Empty, ErrorBanner, Loading, Status, Tile } from '../components/ui.tsx';
import { money, date as fmtDate } from '../lib/format.ts';

type Tab = 'water' | 'waitlist' | 'rates' | 'fleet' | 'crew' | 'unbilled';

const TABS: { id: Tab; label: string }[] = [
  { id: 'water', label: 'The water' },
  { id: 'waitlist', label: 'Waiting list' },
  { id: 'rates', label: 'Rate card' },
  { id: 'fleet', label: 'Fleet' },
  { id: 'crew', label: 'Crew' },
  { id: 'unbilled', label: 'Not yet billed' },
];

const POWER_LABEL: Record<string, string> = {
  none: '—', '15a': '15A', '30a': '30A', '50a': '50A',
  '30a_x2': '2 × 30A', '50a_x2': '2 × 50A',
};

const UNIT_LABEL: Record<string, string> = {
  per_foot_night: 'per foot, per night',
  per_foot_season: 'per foot, per season',
  per_night: 'per night',
  per_season: 'per season',
  per_hour: 'per hour',
  per_day: 'per day',
  per_eighth: 'per eighth of a tank',
  flat: 'flat',
};

const today = () => new Date().toISOString().slice(0, 10);

function Actions({ children }: { children: ReactNode }) {
  return <div className="actions">{children}</div>;
}

export function MarinaAdminPage() {
  const { atLeast } = useSession();
  const canEdit = atLeast('manager');
  const [tab, setTab] = useState<Tab>('water');

  return (
    <>
      <div className="row" style={{ marginBottom: 16 }}>
        <div>
          <h1>Marina</h1>
          <div className="muted small">
            {canEdit
              ? 'The water, the list, and what the dock committee voted on'
              : 'Read only — a manager changes these'}
          </div>
        </div>
      </div>

      <div className="actions" style={{ marginBottom: 14 }}>
        {TABS.map((t) => (
          <button
            key={t.id}
            className={tab === t.id ? 'gold small' : 'secondary small'}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'water' && <Water canEdit={canEdit} />}
      {tab === 'waitlist' && <Waitlist canEdit={canEdit} />}
      {tab === 'rates' && <Rates canEdit={canEdit} />}
      {tab === 'fleet' && <Fleet />}
      {tab === 'crew' && <Crew canEdit={canEdit} />}
      {tab === 'unbilled' && <Unbilled />}
    </>
  );
}

// =====================================================================

interface Slip {
  id: string; code: string; kind: string;
  length_feet: string; beam_feet: string;
  depth_feet: string | null; depth_sounded_on: string | null;
  power: string; has_water: boolean; is_covered: boolean;
  is_reservable: boolean; is_active: boolean;
  dock_id: string; dock_name: string; dock_kind: string; notes: string | null;
}

function Water({ canEdit }: { canEdit: boolean }) {
  const slips = useApi<{ slips: Slip[] }>('/v1/boating/slips?include_inactive=true');
  const docks = useApi<{ docks: { id: string; code: string; name: string; kind: string; slips: string }[] }>(
    '/v1/boating/docks');
  const [adding, setAdding] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  const rows = slips.data?.slips ?? [];
  const byDock = rows.reduce<Record<string, Slip[]>>((acc, s) => {
    (acc[s.dock_name] ??= []).push(s);
    return acc;
  }, {});

  const feet = rows
    .filter((s) => s.is_reservable && s.kind !== 'trailer_space')
    .reduce((n, s) => n + Number(s.length_feet), 0);

  return (
    <>
      <ErrorBanner error={slips.error} />
      <ErrorBanner error={docks.error} />
      {note && <Card><Status tone="good">{note}</Status></Card>}

      <div className="tiles">
        <Tile label="Docks" value={docks.data?.docks.length ?? 0} />
        <Tile label="Slips & spaces" value={rows.length} />
        <Tile label="Rentable feet" value={feet.toLocaleString()}
          note="What a per-foot season rate multiplies" />
      </div>

      {adding && (
        <AddSlipsForm
          docks={docks.data?.docks ?? []}
          onDone={(m) => { setAdding(false); setNote(m); slips.reload(); docks.reload(); }}
          onCancel={() => setAdding(false)}
        />
      )}

      {slips.loading ? (
        <Loading rows={8} />
      ) : rows.length === 0 ? (
        <Card
          title="The Water"
          actions={canEdit && (
            <Actions><button className="small" onClick={() => setAdding(true)}>Add slips</button></Actions>
          )}
        >
          <Empty>No docks yet. Add one, then add the slips on it.</Empty>
        </Card>
      ) : (
        Object.entries(byDock).map(([dock, dockSlips], i) => (
          <Card
            key={dock}
            title={dock}
            flush
            actions={canEdit && i === 0 && (
              <Actions><button className="small" onClick={() => setAdding(true)}>Add slips</button></Actions>
            )}
          >
            <table className="data">
              <thead>
                <tr>
                  <th>Slip</th><th>Kind</th><th>Length</th><th>Beam</th>
                  <th>Depth</th><th>Power</th><th>Water</th><th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {dockSlips.map((s) => (
                  <tr key={s.id} style={{ opacity: s.is_active ? 1 : 0.5 }}>
                    <td className="mono">
                      {s.code}
                      {!s.is_reservable && (
                        <span className="pill" style={{ marginLeft: 6 }}>club</span>
                      )}
                    </td>
                    <td className="small">{s.kind.replace(/_/g, ' ')}</td>
                    <td>{s.length_feet} ft</td>
                    <td>{s.beam_feet} ft</td>
                    <td>
                      {s.depth_feet ? `${s.depth_feet} ft` : <span className="muted">—</span>}
                      {s.depth_sounded_on && (
                        <div className="muted small">sounded {fmtDate(s.depth_sounded_on)}</div>
                      )}
                    </td>
                    <td className="small">{POWER_LABEL[s.power] ?? s.power}</td>
                    <td className="small">{s.has_water ? 'yes' : '—'}</td>
                    <td className="small muted">{s.notes ?? ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        ))
      )}
    </>
  );
}

function AddSlipsForm({
  docks, onDone, onCancel,
}: {
  docks: { id: string; name: string }[];
  onDone: (m: string) => void;
  onCancel: () => void;
}) {
  const [f, setF] = useState({
    dock_id: docks[0]?.id ?? '',
    count: '10', code_prefix: 'A', start_number: '1',
    length_feet: '30', beam_feet: '12',
    depth_feet: '', depth_sounded_on: today(),
    kind: 'slip', power: '30a', has_water: true,
  });

  const add = useMutation(async () => {
    const r = await api<{ slips: { code: string }[] }>('/v1/boating/slips', {
      method: 'POST',
      body: {
        dock_id: f.dock_id,
        count: Number(f.count),
        code_prefix: f.code_prefix,
        start_number: Number(f.start_number),
        length_feet: Number(f.length_feet),
        beam_feet: Number(f.beam_feet),
        depth_feet: f.depth_feet ? Number(f.depth_feet) : null,
        depth_sounded_on: f.depth_feet ? f.depth_sounded_on : null,
        kind: f.kind,
        power: f.power,
        has_water: f.has_water,
      },
    });
    onDone(`Added ${r.slips.length} slips: ${r.slips.map((s) => s.code).join(', ')}.`);
  });

  return (
    <Card title="Add Slips">
      <ErrorBanner error={add.error} />
      <form onSubmit={(e) => { e.preventDefault(); add.run(); }}>
        <div className="grid-2">
          <label className="field">
            <span>Dock</span>
            <select value={f.dock_id} onChange={(e) => setF({ ...f, dock_id: e.target.value })}>
              {docks.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </label>
          <label className="field">
            <span>How many</span>
            <input type="number" min="1" max="60" value={f.count}
              onChange={(e) => setF({ ...f, count: e.target.value })} />
          </label>
          <label className="field">
            <span>Numbered from</span>
            <input value={f.code_prefix}
              onChange={(e) => setF({ ...f, code_prefix: e.target.value.toUpperCase() })} />
          </label>
          <label className="field">
            <span>Starting at</span>
            <input type="number" min="1" value={f.start_number}
              onChange={(e) => setF({ ...f, start_number: e.target.value })} />
          </label>
          <label className="field">
            <span>Length (ft)</span>
            <input type="number" step="0.5" value={f.length_feet}
              onChange={(e) => setF({ ...f, length_feet: e.target.value })} />
          </label>
          <label className="field">
            <span>Beam (ft)</span>
            <input type="number" step="0.5" value={f.beam_feet}
              onChange={(e) => setF({ ...f, beam_feet: e.target.value })} />
          </label>
          <label className="field">
            <span>Controlling depth (ft)</span>
            <input type="number" step="0.5" value={f.depth_feet}
              onChange={(e) => setF({ ...f, depth_feet: e.target.value })} />
          </label>
          <label className="field">
            <span>Sounded on</span>
            <input type="date" value={f.depth_sounded_on}
              onChange={(e) => setF({ ...f, depth_sounded_on: e.target.value })} />
          </label>
          <label className="field">
            <span>Kind</span>
            <select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })}>
              <option value="slip">Slip</option>
              <option value="end_tie">End tie</option>
              <option value="mooring">Mooring</option>
              <option value="dry_rack">Dry rack</option>
              <option value="trailer_space">Trailer space</option>
            </select>
          </label>
          <label className="field">
            <span>Shore power</span>
            <select value={f.power} onChange={(e) => setF({ ...f, power: e.target.value })}>
              <option value="none">None</option>
              <option value="15a">15A</option>
              <option value="30a">30A</option>
              <option value="50a">50A</option>
              <option value="50a_x2">2 × 50A</option>
            </select>
          </label>
        </div>
        <div className="muted small">
          A depth is a sounding with a date on it. On a river it moves — a dry September
          takes a foot off, and the fit check reads this.
        </div>
        <Actions>
          <button type="submit" disabled={add.busy}>{add.busy ? 'Adding…' : 'Add'}</button>
          <button type="button" className="secondary" onClick={onCancel}>Cancel</button>
        </Actions>
      </form>
    </Card>
  );
}

// =====================================================================

interface Entry {
  list_position: number;
  entry_id: string;
  membership_number: string;
  holder_name: string | null;
  applied_on: string;
  waited_days: number;
  priority: number;
  priority_reason: string | null;
  min_length_feet: string | null;
  preferred_dock: string | null;
  boat_name: string | null;
  status: string;
}

function Waitlist({ canEdit }: { canEdit: boolean }) {
  const { currency } = useSession();
  const list = useApi<{
    entries: Entry[];
    policy: {
      offer_expires_after_days: number;
      declines_forfeit_place: boolean;
      lapses_forfeit_place: boolean;
      deposit_amount_minor: number | null;
      holders_may_wait: boolean;
      notes: string | null;
    } | null;
  }>('/v1/boating/waitlist');

  const entries = list.data?.entries ?? [];
  const policy = list.data?.policy;

  return (
    <>
      <ErrorBanner error={list.error} />

      {policy && (
        <Card title="What the Committee Voted">
          <div className="grid-2">
            <div>
              <div className="muted small">An offer lapses after</div>
              <div>{policy.offer_expires_after_days} days</div>
            </div>
            <div>
              <div className="muted small">Saying no costs your place</div>
              <div>{policy.declines_forfeit_place ? 'Yes' : 'No'}</div>
            </div>
            <div>
              <div className="muted small">Saying nothing costs your place</div>
              <div>{policy.lapses_forfeit_place ? 'Yes' : 'No'}</div>
            </div>
            <div>
              <div className="muted small">Deposit to join the list</div>
              <div>{policy.deposit_amount_minor ? money(policy.deposit_amount_minor, currency) : 'None'}</div>
            </div>
          </div>
          {policy.notes && <div className="muted small" style={{ marginTop: 8 }}>{policy.notes}</div>}
        </Card>
      )}

      {list.loading ? (
        <Loading rows={8} />
      ) : entries.length === 0 ? (
        <Card><Empty>Nobody is waiting.</Empty></Card>
      ) : (
        <Card title="Waiting List" flush>
          <table className="data">
            <thead>
              <tr>
                <th>#</th><th>Member</th><th>Asked</th><th>Waited</th>
                <th>Wants</th><th>Boat</th><th>Status</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <tr key={e.entry_id}>
                  <td className="mono">{e.list_position}</td>
                  <td>
                    <strong>{e.holder_name ?? e.membership_number}</strong>
                    <div className="muted small">{e.membership_number}</div>
                    {e.priority !== 0 && e.priority_reason && (
                      <div className="muted small">
                        Moved up: {e.priority_reason}
                      </div>
                    )}
                  </td>
                  <td className="small">{fmtDate(e.applied_on)}</td>
                  <td className="small">
                    {Math.floor(e.waited_days / 365) > 0
                      ? `${Math.floor(e.waited_days / 365)} yr ${Math.floor((e.waited_days % 365) / 30)} mo`
                      : `${Math.floor(e.waited_days / 30)} mo`}
                  </td>
                  <td className="small">
                    {e.min_length_feet ? `${e.min_length_feet} ft or more` : 'anything'}
                    {e.preferred_dock && <div className="muted small">{e.preferred_dock}</div>}
                  </td>
                  <td className="small muted">{e.boat_name ?? 'not bought yet'}</td>
                  <td><span className="pill">{e.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="muted small" style={{ padding: '10px 14px' }}>
            The list is ordered by the day each member asked, and that date is never
            rewritten. A club moves somebody up by giving them a priority, in the open,
            with the reason beside it.
          </div>
        </Card>
      )}
    </>
  );
}

// =====================================================================

interface Rate {
  id: string; fee_kind: string; rate_class: string;
  dock_name: string | null; day_type: string; unit: string;
  amount_minor: number; minimum_minor: number | null;
  revenue_category: string; outlet_name: string | null;
  effective_from: string; effective_to: string | null;
  approved_on: string | null; note: string | null;
}

function Rates({ canEdit }: { canEdit: boolean }) {
  const { currency } = useSession();
  const rates = useApi<{ rates: Rate[] }>('/v1/boating/rates');
  const rows = rates.data?.rates ?? [];
  const unapproved = rows.filter((r) => !r.approved_on).length;

  const byKind = rows.reduce<Record<string, Rate[]>>((acc, r) => {
    (acc[r.fee_kind] ??= []).push(r);
    return acc;
  }, {});

  return (
    <>
      <ErrorBanner error={rates.error} />

      {unapproved > 0 && (
        <Card>
          <Status tone="warning">
            {unapproved} of these {rows.length} rates has no approval date on it. A rate
            the board has not voted on is a rate somebody typed in.
          </Status>
        </Card>
      )}

      {rates.loading ? (
        <Loading rows={10} />
      ) : rows.length === 0 ? (
        <Card><Empty>No rate card yet. Nothing on the dock can be billed until there is one.</Empty></Card>
      ) : (
        Object.entries(byKind).map(([kind, kindRates]) => (
          <Card key={kind} title={kind.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase())} flush>
            <table className="data">
              <thead>
                <tr>
                  <th>Who pays</th><th>Day</th><th>Amount</th><th>Unit</th>
                  <th>Minimum</th><th>Dock</th><th>In force</th><th>Approved</th>
                </tr>
              </thead>
              <tbody>
                {kindRates.map((r) => (
                  <tr key={r.id}>
                    <td>{r.rate_class.replace(/_/g, ' ')}</td>
                    <td className="small">{r.day_type}</td>
                    <td>
                      {r.amount_minor === 0
                        ? <span className="muted">included</span>
                        : money(r.amount_minor, currency)}
                    </td>
                    <td className="small muted">{UNIT_LABEL[r.unit] ?? r.unit}</td>
                    <td className="small">{r.minimum_minor ? money(r.minimum_minor, currency) : '—'}</td>
                    <td className="small">{r.dock_name ?? 'any'}</td>
                    <td className="small">
                      {fmtDate(r.effective_from)}
                      {r.effective_to ? ` – ${fmtDate(r.effective_to)}` : ' onwards'}
                    </td>
                    <td className="small">
                      {r.approved_on
                        ? fmtDate(r.approved_on)
                        : <span className="muted">not voted</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        ))
      )}
    </>
  );
}

// =====================================================================

function Fleet() {
  const fleet = useApi<{
    fleet: {
      rental_boat_id: string; code: string; name: string; class: string;
      make: string | null; model: string | null; capacity: number;
      horsepower: number | null; requires_endorsement: string | null;
      home_slip_code: string | null; is_out_of_service: boolean;
      is_out_now: boolean; out_of_service_until: string | null;
      condition_note: string | null;
    }[];
  }>('/v1/boating/fleet');

  const rows = fleet.data?.fleet ?? [];

  return (
    <>
      <ErrorBanner error={fleet.error} />
      <div className="tiles">
        <Tile label="Boats in the fleet" value={rows.length} />
        <Tile label="On the water now" value={rows.filter((r) => r.is_out_now).length} />
        <Tile label="Out of service" value={rows.filter((r) => r.is_out_of_service).length}
          tone={rows.some((r) => r.is_out_of_service) ? 'warning' : undefined} />
      </div>

      {fleet.loading ? (
        <Loading rows={6} />
      ) : rows.length === 0 ? (
        <Card><Empty>The club owns no boats.</Empty></Card>
      ) : (
        <Card flush>
          <table className="data">
            <thead>
              <tr>
                <th>Boat</th><th>Class</th><th>Capacity</th><th>Power</th>
                <th>Needs</th><th>Lives</th><th>Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.rental_boat_id}>
                  <td>
                    <strong>{r.name}</strong>
                    <div className="muted small">
                      {[r.make, r.model].filter(Boolean).join(' ')}
                    </div>
                  </td>
                  <td className="small">{r.class}</td>
                  <td>{r.capacity}</td>
                  <td className="small">{r.horsepower ? `${r.horsepower} hp` : '—'}</td>
                  <td className="small muted">
                    {r.requires_endorsement
                      ? r.requires_endorsement.replace(/_/g, ' ')
                      : 'anybody may take her'}
                  </td>
                  <td className="mono small">{r.home_slip_code ?? '—'}</td>
                  <td>
                    {r.is_out_now && <span className="pill">on the water</span>}
                    {r.is_out_of_service && (
                      <>
                        <span className="pill">out of service</span>
                        <div className="muted small">
                          {r.condition_note}
                          {r.out_of_service_until && ` — until ${fmtDate(r.out_of_service_until)}`}
                        </div>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </>
  );
}

// =====================================================================

interface CrewMember {
  crew_id: string; role: string; crew_name: string; is_active: boolean;
  is_current: boolean; currency_note: string | null; next_expiry: string | null;
  credentials: {
    credential: string; name: string; reference: string | null;
    issued_on: string | null; expires_on: string | null;
    is_required: boolean; lapsed: boolean;
  }[] | null;
}

function Crew({ canEdit }: { canEdit: boolean }) {
  const crew = useApi<{ crew: CrewMember[] }>('/v1/boating/crew');
  const rows = crew.data?.crew ?? [];
  const lapsed = rows.filter((c) => c.is_active && !c.is_current);

  return (
    <>
      <ErrorBanner error={crew.error} />

      {lapsed.length > 0 && (
        <Card>
          <Status tone="warning">
            {lapsed.length === 1
              ? `${lapsed[0]!.crew_name} cannot be scheduled: `
              : `${lapsed.length} crew cannot be scheduled. `}
            {lapsed.length === 1 && lapsed[0]!.currency_note}
          </Status>
        </Card>
      )}

      {crew.loading ? (
        <Loading rows={6} />
      ) : rows.length === 0 ? (
        <Card><Empty>Nobody on the crew list yet.</Empty></Card>
      ) : (
        rows.map((c) => (
          <Card key={c.crew_id} title={c.crew_name}>
            <div className="row">
              <div>
                <span className="pill">{c.role.replace(/_/g, ' ')}</span>
                {!c.is_active && <span className="pill" style={{ marginLeft: 6 }}>retired</span>}
              </div>
              <div className="spacer" />
              {c.next_expiry && (
                <div className="muted small">next renewal {fmtDate(c.next_expiry)}</div>
              )}
            </div>

            {c.currency_note && (
              <Status tone={c.is_current ? 'warning' : 'serious'}>{c.currency_note}</Status>
            )}

            {(c.credentials ?? []).length > 0 && (
              <table className="data" style={{ marginTop: 8 }}>
                <thead>
                  <tr><th>Certificate</th><th>Reference</th><th>Issued</th><th>Expires</th><th /></tr>
                </thead>
                <tbody>
                  {c.credentials!.map((cr) => (
                    <tr key={cr.credential}>
                      <td>
                        {cr.name}
                        {!cr.is_required && (
                          <span className="muted small"> · not required</span>
                        )}
                      </td>
                      <td className="mono small">{cr.reference ?? '—'}</td>
                      <td className="small">{cr.issued_on ? fmtDate(cr.issued_on) : '—'}</td>
                      <td className="small">
                        {cr.expires_on ? fmtDate(cr.expires_on) : 'does not expire'}
                      </td>
                      <td>{cr.lapsed && <span className="pill">lapsed</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        ))
      )}
    </>
  );
}

// =====================================================================

function Unbilled() {
  const list = useApi<{
    unbilled: { source: string; source_id: string; as_of: string; what: string; who: string | null }[];
  }>('/v1/boating/unbilled');

  const rows = list.data?.unbilled ?? [];

  return (
    <>
      <ErrorBanner error={list.error} />
      {list.loading ? (
        <Loading rows={6} />
      ) : rows.length === 0 ? (
        <Card><Empty>Everything the dock has done has been billed.</Empty></Card>
      ) : (
        <Card title="Not yet Billed" flush>
          <table className="data">
            <thead><tr><th>When</th><th>What</th><th>Who</th><th>Where it lives</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={`${r.source}-${r.source_id}`}>
                  <td className="small">{fmtDate(r.as_of)}</td>
                  <td>{r.what}</td>
                  <td className="small">{r.who ?? <span className="muted">a visitor</span>}</td>
                  <td className="small muted">{r.source.replace(/_/g, ' ')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </>
  );
}
